import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import DynamicTable from "../../../../unicomponents/Tables/DynamicTable";
import DropdownSchoolYear from "../../../../unicomponents/Dropdowns/DropdownSchoolYear";
import DropdownHierarchyOne from "../../../../unicomponents/Dropdowns/DropdownHierarchyOne";
import DropdownHierarchyTwo from "../../../../unicomponents/Dropdowns/DropdownHierarchyTwo";
import DropdownHierarchyThree from "../../../../unicomponents/Dropdowns/DropdownHierarchyThree";
import DropdownUniqueUsersInDisplayData from "../../../../unicomponents/Dropdowns/DropdownUniqueUsersInDisplayData";
import DropdownTermDate from "../../../../unicomponents/Dropdowns/DropdownTermDate";
import useOrganizationSetting from "../../../../hooks/useOrganizationSetting";
import useTermPeriod from "../../../../hooks/useTermPeriod";
import useRegistrationInfo from "../../../../hooks/useRegistrationInfo";
import useLessonStudents from "../../../../hooks/useLessonStudentData";
import { useUIContext } from "../../../../context/UIContext";
import { generateRegistrationRows } from "../../../../util/dealingArrObj";
import { filterSettingsByStudentsPath } from "../../../../util/specialCase";

export default function ApSubjectManagement() {
  const { t } = useTranslation();
  const { setIsLoadingModal, handleToastCenterTop } = useUIContext();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const schoolId = paths[1];

  const [filters, setFilters] = useState({
    schoolYear: null,
    schoolYearId: "",
    hierarchyOneId: "",
    hierarchyTwoId: "",
    hierarchyThreeId: "",
    organPathForSort: "/",
    termId: "",
    selectedStudent: "",
  });

  const [termId, setTermId] = useState("");
  const [studentList, setStudentList] = useState([]);
  const [newSetting, setNewSetting] = useState([]);
  const [lessonStudentsArray, setLessonStudentsArray] = useState([]);
  const [apSettings, setApSettings] = useState({}); // lesson_id별 AP 설정 관리
  const [savedLessons, setSavedLessons] = useState([]); // 저장된 과목들 표시용

  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting({ schoolid: schoolId });

  const {
    termPeriodQuery: { data: termPeriodData },
  } = useTermPeriod({ schoolid: schoolId });

  const {
    registrationInfoQuery: { data: registrationInfoData },
  } = useRegistrationInfo({ schoolid: schoolId }, { registrationInfo: true });

  const { getAllLessonStudentsByStudentId, updateLessonStudents } =
    useLessonStudents();
  const getAllLessonStudentsByStudentIdRef = useRef(
    getAllLessonStudentsByStudentId
  );

  // 초기 설정 데이터 처리
  useEffect(() => {
    if (registrationInfoData && settingData) {
      setNewSetting(
        filterSettingsByStudentsPath(registrationInfoData, settingData)
      );
    }
  }, [registrationInfoData, settingData]);

  // 학년도 및 학생 리스트 설정
  useEffect(() => {
    let initialSchoolYear = null;
    let initialSchoolYearId = null;
    let initialData = [];

    if (
      filters.schoolYear === null &&
      newSetting &&
      newSetting.length > 0 &&
      registrationInfoData
    ) {
      initialData = newSetting
        .sort((a, b) => a.hierarchy_level - b.hierarchy_level)
        .sort((a, b) => b.school_year - a.school_year);

      if (initialData.length < 1) {
        alert(t("common.messages.registrationError"));
        return;
      }

      initialSchoolYear = initialData[0].school_year;
      initialSchoolYearId = initialData[0].id;

      setFilters((filters) => ({
        ...filters,
        schoolYear: initialSchoolYear,
        schoolYearId: initialSchoolYearId,
      }));

      if (initialSchoolYear) {
        //학생 리스트
        const sortedRows = generateRegistrationRows(
          registrationInfoData,
          newSetting,
          initialSchoolYear,
          filters.organPathForSort
        );
        setStudentList(sortedRows);
      }
    } else {
      //rows 데이터처리
      if (registrationInfoData && newSetting) {
        const sortedRows = generateRegistrationRows(
          registrationInfoData,
          newSetting,
          filters.schoolYear,
          filters.organPathForSort
        );

        setStudentList(sortedRows);
      }
    }
  }, [
    filters.schoolYear,
    filters.organPathForSort,
    registrationInfoData,
    newSetting,
    termPeriodData,
    t,
  ]);

  // AP 설정 초기화 함수
  const initializeApSettings = useCallback(
    (lessonData) => {
      const initialSettings = {};
      lessonData.forEach((lesson) => {
        const lessonId = lesson.lesson_id;
        const existingAp = lesson.lesson_management?.ap || {};

        // 기존 데이터에서 학년도와 학기 정보 복원
        let schoolYear = existingAp.schoolYear || "";
        let termName = existingAp.termName || "";

        // school_year_id가 있으면 해당하는 학년도 찾기
        if (existingAp.school_year_id && !schoolYear) {
          const foundSetting = newSetting.find(
            (s) => s.id.toString() === existingAp.school_year_id.toString()
          );
          if (foundSetting) {
            schoolYear = foundSetting.school_year;
          }
        }

        // term_id가 있으면 해당하는 학기 찾기
        if (existingAp.term_id && !termName) {
          const foundTerm = termPeriodData?.find(
            (t) => t.id.toString() === existingAp.term_id.toString()
          );
          if (foundTerm) {
            termName = foundTerm.term_name;
          }
        }

        initialSettings[lessonId] = {
          applied: existingAp.applied || false,
          school_year_id: existingAp.school_year_id || "",
          schoolYear: schoolYear,
          term_id: existingAp.term_id || "",
          termName: termName,
        };
      });
      setApSettings(initialSettings);
    },
    [newSetting, termPeriodData]
  );

  // 선택된 학생의 수업 데이터 가져오기
  useEffect(() => {
    if (filters.selectedStudent && termId) {
      const forWhere = {
        schoolid: schoolId,
        username: filters.selectedStudent,
      };

      getAllLessonStudentsByStudentIdRef.current.mutate(forWhere, {
        onSuccess: (data) => {
          if (data && data.length > 0) {
            const filteredData = data.filter(
              (item) =>
                Number(item.school_year_id) === Number(filters.schoolYearId) &&
                item.lesson?.TermDates?.term_name?.toString() ===
                  filters.termName?.toString()
            );
            setLessonStudentsArray(filteredData);

            // AP 설정 초기화
            initializeApSettings(filteredData);

            handleClearSelection();
          }
        },
        onError: (error) => {
          console.error("수업 데이터 로딩 에러:", error);
          alert(t("common.messages.errorAlert") + error.message);
        },
      });
    } else {
      setLessonStudentsArray([]);
    }
  }, [
    filters.selectedStudent,
    filters.schoolYearId,
    filters.termName,
    schoolId,
    t,
    initializeApSettings,
    termId,
  ]);

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const selectedOptionText = e.target.selectedOptions[0].text;
    const dataName = e.target.selectedOptions[0].getAttribute("data-name");
    const dataTermName =
      e.target.selectedOptions[0].getAttribute("data-term-name");
    let organPath = "";

    if (!dataName) {
      organPath = "N/A";
    } else {
      organPath = dataName + "/" + value;
    }

    if (name === "0") {
      setFilters((filters) => ({
        ...filters,
        schoolYear: selectedOptionText,
        schoolYearId: value,
        hierarchyOneId: "",
        organPathForSort: "N/A",
        selectedStudent: "",
      }));
    }

    if (name === "1") {
      setFilters((filters) => ({
        ...filters,
        hierarchyOneId: value,
        hierarchyTwoId: "",
        hierarchyThreeId: "",
        organPathForSort: "N/A",
        selectedStudent: "",
      }));
    }

    if (name === "2") {
      setFilters((filters) => ({
        ...filters,
        hierarchyTwoId: value,
        hierarchyThreeId: "",
        organPathForSort: "N/A",
        selectedStudent: "",
      }));
    }

    if (name === "3") {
      setFilters((filters) => ({
        ...filters,
        hierarchyThreeId: value,
        organPathForSort: organPath,
        selectedStudent: "",
      }));
    }

    if (name === "students") {
      setFilters((filters) => ({
        ...filters,
        selectedStudent: value,
      }));
    }

    if (name === "termDate") {
      setFilters((filters) => ({
        ...filters,
        termId: value,
        termName: dataTermName,
      }));

      setTermId(value);
    }
  };

  const handleClearSelection = () => {
    // 더 이상 선택 관련 상태가 없음
  };

  // AP 설정 변경 핸들러
  const handleApSettingChange = (lessonId, field, value) => {
    setApSettings((prev) => ({
      ...prev,
      [lessonId]: {
        ...prev[lessonId],
        [field]: value,
      },
    }));
  };

  // 학년도 선택 시 해당 학년도 텍스트도 업데이트
  const handleSchoolYearChange = (lessonId, schoolYearId) => {
    const selectedSetting = newSetting.find(
      (s) => s.id.toString() === schoolYearId
    );
    const schoolYear = selectedSetting ? selectedSetting.school_year : "";

    setApSettings((prev) => ({
      ...prev,
      [lessonId]: {
        ...prev[lessonId],
        school_year_id: schoolYearId,
        schoolYear: schoolYear,
      },
    }));
  };

  // 학기 선택 시 해당 학기 텍스트도 업데이트
  const handleTermChange = (lessonId, termId) => {
    const selectedTerm = termPeriodData?.find(
      (t) => t.id.toString() === termId
    );
    const termName = selectedTerm ? selectedTerm.term_name : "";

    setApSettings((prev) => ({
      ...prev,
      [lessonId]: {
        ...prev[lessonId],
        term_id: termId,
        termName: termName,
      },
    }));
  };

  // AP 설정 저장 함수
  const handleSaveApSetting = (lessonStudent) => {
    const lessonId = lessonStudent.lesson_id;
    const currentApSetting = apSettings[lessonId];

    if (!currentApSetting) {
      handleToastCenterTop("AP 설정 데이터가 없습니다.");
      return;
    }

    // 기존 lesson_management 데이터 보존하면서 ap만 업데이트
    const existingLessonManagement = lessonStudent.lesson_management || {};
    const updatedLessonManagement = {
      ...existingLessonManagement,
      ap: currentApSetting,
    };

    const updateData = {
      id: lessonStudent.id,
      lesson_management: updatedLessonManagement,
    };

    setIsLoadingModal(true);
    updateLessonStudents.mutate(updateData, {
      onSuccess: (successMessage) => {
        console.log(successMessage);
        setIsLoadingModal(false);
        setSavedLessons((prev) => [...prev, lessonId]);
        handleToastCenterTop("AP 설정이 저장되었습니다.");

        // 1초 후 저장 표시 제거
        setTimeout(() => {
          setSavedLessons((prev) => prev.filter((id) => id !== lessonId));
        }, 1000);

        // 데이터 다시 로드하여 최신 상태 반영
        const forWhere = {
          schoolid: schoolId,
          username: filters.selectedStudent,
        };
        getAllLessonStudentsByStudentIdRef.current.mutate(forWhere, {
          onSuccess: (data) => {
            if (data && data.length > 0) {
              const filteredData = data.filter(
                (item) =>
                  Number(item.school_year_id) ===
                    Number(filters.schoolYearId) &&
                  item.lesson?.TermDates?.term_name?.toString() ===
                    filters.termName?.toString()
              );
              setLessonStudentsArray(filteredData);
              initializeApSettings(filteredData);
            }
          },
        });
      },
      onError: (error) => {
        setIsLoadingModal(false);
        console.error("AP 설정 저장 에러:", error);
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
  };

  // DynamicTable 설정
  const tableConfig = {
    style: { fontSize: "14px" },
    headers: [
      {
        label: "과목명",
        style: { width: "200px", textAlign: "left" },
        sortField: "lesson.subject_name",
      },
      {
        label: "시수",
        style: { width: "60px", textAlign: "center" },
      },
      {
        label: "담당교사",
        style: { width: "120px", textAlign: "left" },
      },
      {
        label: "점수",
        style: { width: "80px", textAlign: "center" },
      },
      {
        label: "AP수업적용",
        style: { width: "100px", textAlign: "center" },
      },
      {
        label: "대체학년도",
        style: { width: "80px", textAlign: "center" },
      },
      {
        label: "대체학기",
        style: { width: "80px", textAlign: "center" },
      },
      {
        label: "적용",
        style: { width: "80px", textAlign: "center" },
      },
    ],
    columns: [
      {
        valueKey: "lesson.subject_name",
        cellStyle: { textAlign: "left", paddingLeft: "8px" },
      },
      {
        valueKey: "lesson.lesson_hours",
        cellStyle: { textAlign: "center" },
      },
      {
        valueFormatter: (value, item) => {
          const teachers = item.lesson?.teachers;
          if (!teachers || !Array.isArray(teachers)) return "";
          return teachers
            .map((teacher) => teacher.tname || teacher.name || teacher)
            .join(", ");
        },
        cellStyle: { textAlign: "left", paddingLeft: "8px" },
      },
      {
        valueKey: "grade",
        cellStyle: { textAlign: "center" },
      },
      {
        valueFormatter: (value, item) => {
          const lessonId = item.lesson_id;
          const currentSetting = apSettings[lessonId] || {};
          // apSettings에 값이 있으면 우선 사용, 없으면 저장된 데이터 사용
          const isApplied = currentSetting.hasOwnProperty("applied")
            ? currentSetting.applied
            : item.lesson_management?.ap?.applied || false;

          return (
            <div className="text-center">
              <input
                type="checkbox"
                checked={isApplied}
                onChange={(e) =>
                  handleApSettingChange(lessonId, "applied", e.target.checked)
                }
                className="form-check-input"
              />
            </div>
          );
        },
        cellStyle: { textAlign: "center" },
      },
      {
        valueFormatter: (value, item) => {
          const lessonId = item.lesson_id;
          const currentSetting = apSettings[lessonId] || {};
          // apSettings에 값이 있으면 우선 사용, 없으면 저장된 데이터 사용
          const selectedSchoolYearId = currentSetting.hasOwnProperty(
            "school_year_id"
          )
            ? currentSetting.school_year_id
            : item.lesson_management?.ap?.school_year_id || "";

          return (
            <div className="text-center">
              <div style={{ fontSize: "12px" }}>
                <DropdownSchoolYear
                  settingData={newSetting}
                  handleDropdownChange={(e) =>
                    handleSchoolYearChange(lessonId, e.target.value)
                  }
                  schoolYearId={selectedSchoolYearId}
                  allYears={false}
                  title="선택"
                />
              </div>
            </div>
          );
        },
        cellStyle: { textAlign: "center" },
      },
      {
        valueFormatter: (value, item) => {
          const lessonId = item.lesson_id;
          const currentSetting = apSettings[lessonId] || {};
          // apSettings에 값이 있으면 우선 사용, 없으면 저장된 데이터 사용
          const selectedTermId = currentSetting.hasOwnProperty("term_id")
            ? currentSetting.term_id
            : item.lesson_management?.ap?.term_id || "";

          return (
            <div className="text-center">
              <div style={{ fontSize: "12px" }}>
                <DropdownTermDate
                  termPeriodData={termPeriodData}
                  handleDropdownChange={(e) =>
                    handleTermChange(lessonId, e.target.value)
                  }
                  termId={selectedTermId}
                  schoolYearId={
                    currentSetting.hasOwnProperty("school_year_id")
                      ? currentSetting.school_year_id
                      : item.lesson_management?.ap?.school_year_id ||
                        filters.schoolYearId
                  }
                  isTitle={true}
                />
              </div>
            </div>
          );
        },
        cellStyle: { textAlign: "center" },
      },
      {
        valueFormatter: (value, item) => {
          const lessonId = item.lesson_id;
          const isSaved = savedLessons.includes(lessonId);
          return (
            <div className="text-center">
              <button
                className={`btn btn-sm ${
                  isSaved ? "btn-success" : "btn-primary"
                }`}
                onClick={() => handleSaveApSetting(item)}
                style={{ fontSize: "12px", padding: "2px 8px" }}
              >
                {isSaved ? "저장완료" : "저장"}
              </button>
            </div>
          );
        },
        cellStyle: { textAlign: "center" },
      },
    ],
    rows: lessonStudentsArray,
  };

  return (
    <div className="p-4">
      {/* 헤더 */}
      <div className="row mb-4">
        <div className="col-auto">
          <h3 className="fw-bold">AP과목 조정</h3>
        </div>
      </div>

      {/* 드롭다운 필터 섹션 */}
      <div
        className="d-flex justify-content-end gap-2 flex-wrap mb-4"
        style={{ width: "100%" }}
      >
        <div style={{ width: "95px" }} className="me-1">
          <DropdownSchoolYear
            settingData={newSetting}
            handleDropdownChange={handleDropdownChange}
            schoolYearId={filters.schoolYearId}
            allYears={false}
          />
        </div>
        <div style={{ width: "95px" }} className="me-1">
          <DropdownHierarchyOne
            settingData={settingData}
            handleDropdownChange={handleDropdownChange}
            schoolYear={filters.schoolYear}
            hierarchyOneId={filters.hierarchyOneId}
            extraOption={false}
          />
        </div>
        <div style={{ width: "95px" }} className="me-1">
          <DropdownHierarchyTwo
            settingData={settingData}
            handleDropdownChange={handleDropdownChange}
            schoolYear={filters.schoolYear}
            hierarchyOneId={filters.hierarchyOneId}
            hierarchyTwoId={filters.hierarchyTwoId}
            extraOption={false}
            disabled={!filters.hierarchyOneId}
          />
        </div>
        <div style={{ width: "95px" }} className="me-1">
          <DropdownHierarchyThree
            settingData={settingData}
            handleDropdownChange={handleDropdownChange}
            schoolYear={filters.schoolYear}
            hierarchyTwoId={filters.hierarchyTwoId}
            hierarchyThreeId={filters.hierarchyThreeId}
            extraOption={false}
            disabled={!filters.hierarchyTwoId}
          />
        </div>
        <div style={{ width: "120px" }} className="me-1">
          <DropdownUniqueUsersInDisplayData
            displayData={studentList}
            displayKey={"fullname"}
            valueKey={"username"}
            filters={{ organization_path: filters.organPathForSort }}
            uniqueKey={"username"}
            sortKey={"studentno"}
            isDesc={false}
            handleDropdownChange={handleDropdownChange}
            selectedUser={filters.selectedStudent}
            selectName={"students"}
            styleClass={"form-control"}
            dropdownTitle={t("common.user.student") || "학생"}
            required={true}
            disabled={!filters.hierarchyThreeId}
          />
        </div>
        <div style={{ width: "95px" }}>
          <DropdownTermDate
            termPeriodData={termPeriodData}
            handleDropdownChange={handleDropdownChange}
            termId={termId}
            schoolYearId={filters.schoolYearId}
          />
        </div>
      </div>
      {/* 테이블 */}
      {filters.selectedStudent ? (
        lessonStudentsArray.length > 0 ? (
          <div className="table-responsive">
            <DynamicTable
              tableConfig={tableConfig}
              dataStructure="flat"
              tableStyle="cleanModern"
              sortable={true}
              initialSortField="lesson.subject_name"
              initialSortDirection="asc"
            />
          </div>
        ) : (
          <div className="alert alert-info mt-4">
            <p>해당 학생의 수업 데이터가 없습니다.</p>
          </div>
        )
      ) : (
        <div className="alert alert-info mt-4">
          <h5>학생을 선택해주세요</h5>
          <p>
            학년도, 과정, 학년, 반, 학생을 순서대로 선택하면 해당 학생의
            AP과목을 조정할 수 있습니다.
          </p>
        </div>
      )}
    </div>
  );
}
